export interface IPatient {
    id: 3;
    name: string;
    birthDate: Date | null;
    isAccessible: boolean;
    isDeletable: boolean;
    status: string;
    therapeuticIndicationId: number;
    sexID: number;
    email: string;
    fileNumber?: string | null;
    primaryHcpName?: string;
    isPrimaryHcp?: boolean;
}

export class Patient {
    public id: number;
    public name: string;
    public birthDate: Date | null;
    public isAccessible: boolean;
    public isDeletable: boolean;
    public status: string;
    public therapeuticIndicationId: number;
    public sexID: number;
    public email: string;
    public fileNumber?: string | null;
    public primaryHcpName?: string;
    public isPrimaryHcp?: boolean;

    constructor(input: IPatient) {
        this.id = input.id;
        this.name = input.name;
        this.birthDate = input.birthDate;
        this.isAccessible = input.isAccessible;
        this.isDeletable = input.isDeletable;
        this.status = input.status;
        this.therapeuticIndicationId = input.therapeuticIndicationId;
        this.sexID = input.sexID;
        this.email = input.email;
        this.fileNumber = input.fileNumber;
        this.primaryHcpName = input.primaryHcpName;
        this.isPrimaryHcp = input.isPrimaryHcp;
    }
}

export interface IPatientProfiles {
    sexID: number;
    therapeuticIndicationID?: number;
    birthDate: Date | undefined;
    address: string;
    address2: string | undefined;
    city: string;
    province: string;
    country: string;
    postalCode: string;
    phoneNumber: string;
    allowSMS: boolean;
    smsOptOutSource?: number | null;
    smsPhoneNumber?: string | null;
    firstName: string;
    lastName: string;
    language: string;
    contactableForResearch: boolean;
    isAnonymizedMode?: boolean;
    primaryHcpName?: string;
    therapeuticIndicationName?: string;
    languages: {
        code: string;
        name: string;
    }[];
    sexes: {
        id: number;
        name: string;
    }[];
    hiddenRegistrationFields?: string[];
}

export class PatientProfiles {
    public sexID: number;
    public therapeuticIndicationID?: number;
    public birthDate: Date | undefined;
    public address: string;
    public address2: string | undefined;
    public city: string;
    public province: string;
    public country: string;
    public postalCode: string;
    public phoneNumber: string;
    public allowSMS: boolean;
    public smsOptOutSource?: number | null;
    public smsPhoneNumber?: string | null;
    public firstName: string;
    public lastName: string;
    public language: string;
    public contactableForResearch: boolean | null;
    public isAnonymizedMode?: boolean;
    public primaryHcpName?: string;
    public therapeuticIndicationName?: string;
    public languages: {
        code: string;
        name: string;
    }[];
    public sexes: {
        id: number;
        name: string;
    }[];
    public hiddenRegistrationFields: string[];

    constructor(input: IPatientProfiles) {
        this.sexID = input.sexID;
        this.therapeuticIndicationID = input.therapeuticIndicationID;
        this.birthDate = input.birthDate ? new Date(input.birthDate) : undefined;
        this.address = input.address;
        this.address2 = input.address2;
        this.city = input.city;
        this.province = input.province;
        this.country = input.country;
        this.postalCode = input.postalCode;
        this.phoneNumber = input.phoneNumber;
        this.allowSMS = input.allowSMS;
        this.smsOptOutSource = input.smsOptOutSource;
        this.smsPhoneNumber = input.smsPhoneNumber;
        this.firstName = input.firstName;
        this.lastName = input.lastName;
        this.language = input.language;
        this.contactableForResearch = input.contactableForResearch;
        this.isAnonymizedMode = input.isAnonymizedMode;
        this.primaryHcpName = input.primaryHcpName;
        this.therapeuticIndicationName = input.therapeuticIndicationName;
        this.languages = input.languages;
        this.sexes = input.sexes;
        this.hiddenRegistrationFields = input.hiddenRegistrationFields ?? [];
    }

    isFieldHidden(fieldKey: string): boolean {
        return this.hiddenRegistrationFields.includes(fieldKey);
    }

    get sexesCombobox(): Array<{ value: string; label: string }> {
        return this.toCombobox(this.sexes);
    }

    private toCombobox(items: Array<{ id: number; name: string }> = []): Array<{ value: string; label: string }> {
        return items.map(({ id, name }) => ({ value: id.toString(), label: name }));
    }
}

export interface IPatientProfileSummary {
    name: string;
    birthDate: string | null;
    sexID: number | null;
    sex: string | null;
    therapeuticIndication: string | null;
    primaryHcpName: string | null;
    email: string;
    fileNumber: string | null;
}

export interface IPatientsRegisterPayload {
    firstName: string;
    lastName: string;
    password: string;
    email: string;
    invitationToken: string;
}

export interface IPatientsInitialProfilePayload {
    sexID?: number;
    birthDate?: Date;
    address?: string;
    address2?: string;
    city?: string;
    province?: string;
    country?: string;
    postalCode?: string;
    phoneNumber?: string;
    allowSMS?: boolean;
    language: string;
    contactableForResearch: boolean;
}

export interface IPatientsProfilePayload extends IPatientsInitialProfilePayload {
    firstName: string;
    lastName: string;
}
